import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { useSalesOptions } from 'hooks/useSalesOptions';
import { useSalesService } from 'services/sales';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { SalesOptions, Customer } from 'types/sales';
import { syncAllPendingTransactions, isOnline } from 'utils/posSyncService';
import { CartItem } from 'utils/posIndexedDBUtils';

interface POSContextType {
  salesOptions: SalesOptions | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  // Convenience getters
  products: SalesOptions['products'];
  customers: SalesOptions['customers'];
  paymentMethods: SalesOptions['paymentMethods'];
  // Shared POS state
  cart: CartItem[];
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  selectedCustomer: Customer | null;
  setSelectedCustomer: React.Dispatch<React.SetStateAction<Customer | null>>;
  customerSearchQuery: string;
  setCustomerSearchQuery: React.Dispatch<React.SetStateAction<string>>;
}

const POSContext = createContext<POSContextType | undefined>(undefined);

export function usePOS() {
  const context = useContext(POSContext);
  if (context === undefined) {
    throw new Error('usePOS must be used within a POSProvider');
  }
  return context;
}

interface POSProviderProps {
  children: ReactNode;
}

export function POSProvider({ children }: POSProviderProps) {
  // Load sales options once at the provider level
  const { salesOptions, loading, error, reload } = useSalesOptions(true);
  const salesService = useSalesService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);

  // Shared state for POS
  const [cart, setCart] = React.useState<CartItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] =
    React.useState<Customer | null>(null);
  const [customerSearchQuery, setCustomerSearchQuery] = React.useState('');

  // Clear state when company changes
  useEffect(() => {
    setCart([]);
    setSelectedCustomer(null);
    setCustomerSearchQuery('');
  }, [activeCompanyId]);

  // Background sync function - only sync transactions for the active company
  const syncPending = useCallback(async () => {
    if (isOnline() && activeCompanyId) {
      await syncAllPendingTransactions(activeCompanyId, salesService);
    }
  }, [salesService, activeCompanyId]);

  // Monitor online/offline status and sync when coming back online
  useEffect(() => {
    const handleOnline = () => {
      // Sync all pending transactions when coming back online
      void syncPending();
    };

    window.addEventListener('online', handleOnline);

    // Also attempt sync on mount if online
    if (isOnline()) {
      void syncPending();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncPending]);

  // Periodic sync check (every 30 seconds if online)
  useEffect(() => {
    if (!isOnline()) return;

    const interval = setInterval(() => {
      void syncPending();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [syncPending]);

  // Extract and memoize convenience properties
  const value = useMemo(
    () => ({
      salesOptions,
      loading,
      error,
      reload,
      products: salesOptions?.products ?? [],
      customers: salesOptions?.customers ?? [],
      paymentMethods: salesOptions?.paymentMethods ?? [],
      cart,
      setCart,
      selectedCustomer,
      setSelectedCustomer,
      customerSearchQuery,
      setCustomerSearchQuery,
    }),
    [
      salesOptions,
      loading,
      error,
      reload,
      cart,
      selectedCustomer,
      customerSearchQuery,
    ],
  );

  return <POSContext.Provider value={value}>{children}</POSContext.Provider>;
}
